import { CellRenderer } from '@dealstack/frontend/shared/components/Investors/Substeps/VestingTable/CellRenderer';
import { ColumnRenderer } from '@dealstack/frontend/shared/components/Investors/Substeps/VestingTable/ColumnRenderer';
import { ColumnDefinition } from '@dealstack/frontend/shared/components/Table/Table/types';
import { useTable } from '@dealstack/frontend/shared/components/Table/Table/useTable';
import {
	PostInvestorDraftInstanceRes,
	VestingTermSimplifiedRes,
} from '@dealstack/shared/lib/generated';
import { useCallback, useMemo, useState } from 'react';
import { DropdownInput } from '@dealstack/frontend/shared/components/DropdownInput/DropdownInput';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';

export const useVestingTable = (
	onVestingTermChange: (investorId: string) => (vestingTermId: string) => void,
	investors?: PostInvestorDraftInstanceRes[],
	vestingTerms?: VestingTermSimplifiedRes[],
	vestingTermsChosen?: Map<string, string>
) => {
	const commonUiTranslation = useTranslationCommonUi();

	const [bulkUpdate, setBulkUpdate] = useState(false);
	const [bulkUpdateSelectedIds, setBulkUpdateSelectedIds] = useState<Set<string>>();

	const openBulkUpdate = () => {
		setBulkUpdate(true);
	};

	const onBulkUpdate = (vestingTermId: string) => {
		for (const investorId of bulkUpdateSelectedIds ?? []) {
			onVestingTermChange(investorId)(vestingTermId);
		}

		setBulkUpdate(false);
	};

	const onSelectChange = (selectedIds: Set<string>) => {
		setBulkUpdateSelectedIds(selectedIds);
	};

	const columns: ColumnDefinition[] = useMemo(
		() => [
			{
				id: 'name',
				label: commonUiTranslation('investors.name'),
				sortable: true,
				pinned: true,
				width: 150,
			},

			{
				id: 'contact',
				label: commonUiTranslation('investors.contact'),
				pinned: true,
				width: 300,
				subcolumns: [
					{
						id: 'email',
						label: commonUiTranslation('investors.email'),
						sortable: true,
					},
				],
			},
			{
				id: 'vesting',
				label: commonUiTranslation('investors.vesting_terms'),
				width: 250,
				pinned: false,
				subcolumns: [
					{
						id: 'vesting-button',
						label: '',
						sortable: true,
					},
				],
			},
		],
		[]
	);

	const data = useMemo(
		() => [
			...(investors?.map((investor) => ({
				...investor,
				id: investor.email,
				contact: {
					email: investor.email,
				},
				name: `${investor.firstName} ${investor.lastName}`,
			})) ?? []),
		],
		[investors]
	);

	const tableInstance = useTable({
		columns,
		data,
		totalCount: data.length,
		tableHeight: 500,
		rowSize: 56,
		rowSelect: bulkUpdate,
		customCellValueFn: useCallback(
			(column, item) => {
				if (column.id === 'name') {
					return item.name;
				} else if (column.id === 'email') {
					return item.contact.email;
				} else if (column.id.startsWith('vesting')) {
					return (
						<DropdownInput
							options={
								vestingTerms?.map((vestingTerm) => ({
									label: vestingTerm.name,
									value: vestingTerm.id,
								})) ?? []
							}
							value={vestingTermsChosen?.get(item.contact.email) ?? ''}
							onChange={onVestingTermChange(item.contact.email)}
							disabled={bulkUpdate}
						/>
					);
				} else {
					return '??';
				}
			},
			[vestingTermsChosen, vestingTerms, bulkUpdate]
		),
		CustomCellRenderer: CellRenderer,
		CustomColumnRenderer: ColumnRenderer({
			openBulkUpdate,
			bulkUpdate,
			onBulkUpdate,
			vestingTerms,
			closeBulkUpdate: () => setBulkUpdate(false),
			tCommonUi: commonUiTranslation,
		}),
		onSelectChange,
	});

	return { tableInstance };
};
